import {ANTIBODY_SCHEMA_INTERFACE_ID} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {Permissions as BenchlingPermissions} from 'auth/PermissionConstants';
import ModelTypeConstants, {SequenceSubtype} from 'constants/ModelTypeConstants';
import {SchemaInterfaceExtendingSchema} from 'constants/RegistryConstants';
import normalizeFolder from 'folders/util/normalizeFolder';
import {TagSchemaSequenceSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {graphQLArrayToREST, graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {EQUIPMENT_SCHEMA_INTERFACE_ID} from 'instruments/equipment/EquipmentConstants';
import {ComplexPolymerInterfaceApiId} from 'modbio/complex-polymers/ComplexPolymerSchemaInterfaceConstants';
import {
  ItemListingQuery,
  ItemListingQueryVariables,
} from 'nav/sidepanel/listing/__generated/ItemListingQuery';
import {ItemListingSchemasQuery} from 'nav/sidepanel/listing/__generated/ItemListingSchemasQuery';
import {AssayRun} from 'results/assays/AssayRecordConstants';
import {SidepanelRun} from 'runs/sidepanel/RunsSidepanelConstants';
import {graphQLStorableSummaryToREST} from 'samples/storables/graphql/translateStorable';
import {Schema} from 'schemas/SchemaConstants';
import {TypesFilterSchemasQuery} from 'search/filters/__generated/TypesFilterSchemasQuery';
import {TypesFilterTagSchemasSSIQuery} from 'search/filters/__generated/TypesFilterTagSchemasSSIQuery';
import {TypesMenuSchema} from 'search/filters/TypesAndSchemaFilterTypes';
import {BaseItem, ItemResult, ListableItem} from 'search/SearchConstants';
import {BasicFilterableTagSchema, tagSchemaMatchesBasicFilters} from 'tag-schema/filterTagSchemas';
import {
  isDNAOligoSchema,
  isDNASequenceSchema,
  isRNAOligoSchema,
  isRNASequenceSchema,
} from 'tag-schema/util/TagSchemaUtil';
import APIUtil from 'util/APIUtil';
import {readableTime} from 'util/DateUtil';

type TypesFilterTagSchema = TypesFilterSchemasQuery['tagSchemas'][number];
type ItemListingTagSchema = ItemListingSchemasQuery['tagSchemas'][number];
type ItemListingStudySchema = ItemListingSchemasQuery['studySchemas'][number];
type ItemListingRunSchema = ItemListingSchemasQuery['runSchemas'][number];
type ItemListingResultSchema = ItemListingSchemasQuery['resultSchemas'][number];
type ItemListingRequestSchema = ItemListingSchemasQuery['requestSchemas'][number];
type ItemListingProcessTaskSchema =
  ItemListingSchemasQuery['processTaskSchemasSupportingPlatformFeature'][number];
type ItemListingProcessOutputSchema =
  ItemListingSchemasQuery['processOutputSchemasSupportingPlatformFeature'][number];
type ItemListingPlateSchema = ItemListingSchemasQuery['plateSchemas'][number];
type ItemListingLocationSchema = ItemListingSchemasQuery['locationSchemas'][number];
type ItemListingContainerSchema = ItemListingSchemasQuery['containerSchemas'][number];
type ItemListingConnectionSchema = ItemListingSchemasQuery['connectionSchemas'][number];
type ItemListingBoxSchema = ItemListingSchemasQuery['boxSchemas'][number];

export function idsToItemListingQueryParams(ids: Array<string>): ItemListingQueryVariables {
  return {
    analysisIds: ids.filter((id) => APIUtil.isAnalysis(id)),
    analyticsDashboardIds: ids.filter((id) => APIUtil.isAnalyticsDashboard(id)),
    bioentityIds: ids.filter((id) => APIUtil.isBiologicalEntity(id)),
    bulkAssemblyIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.BULK_ASSEMBLY),
    collectibleIds: ids.filter((id) => APIUtil.isTemplateCollectible(id)),
    connectionIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.CONNECTION),
    datasetIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.DATASET),
    entryIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.ENTRY),
    fileIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.FILE),
    folderIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.FOLDER),
    jellyfishIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.JELLYFISH),
    processIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.PROCESS),
    processOutputIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.PROCESS_OUTPUT),
    processTaskIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.PROCESS_TASK),
    proteinAlignmentIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.PROTEIN_ALIGNMENT),
    protocolIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.PROTOCOL),
    reactionIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.REACTION),
    requestIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.REQUEST),
    requestV2SubmissionIds: ids.filter((id) => APIUtil.isRequestV2Submission(id)),
    runIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.ASSAY_RUN),
    sequenceAlignmentIds: ids.filter(
      (id) => APIUtil.apiIdToType(id) === ModelTypeConstants.SEQUENCE_ANALYSIS
    ),
    stageEntryIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.STAGE_ENTRY),
    storableIds: ids.filter((id) => APIUtil.isStorable(id)),
    studyIds: ids.filter((id) => APIUtil.isStudy(id)),
    templateCollectionIds: ids.filter((id) => APIUtil.isTemplateCollection(id)),
    testDefinitionIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.TEST_DEFINITION),
    testOrderIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.TEST_ORDER),
    workflowIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.WORKFLOW),
    worklistIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.WORKLIST),
    worksheetIds: ids.filter((id) => APIUtil.apiIdToType(id) === ModelTypeConstants.WORKSHEET),
  };
}

function sidepanelRun(run: AssayRun): SidepanelRun {
  return {
    ...run,
    id: run.apiId!,
    name: `${run.schemaName} ${readableTime(run.createdAt)}`,
    warehouseId: run.id,
  };
}

export function queryResultsToItems(queryResults: ItemListingQuery): Array<BaseItem> {
  return [
    ...accessibleObjs(queryResults.analyses),
    ...accessibleObjs(queryResults.analyticsDashboards),
    ...graphQLArrayToREST('BioentitySummary', accessibleObjs(queryResults.bioentities)),
    ...graphQLArrayToREST('EntrySummary', accessibleObjs(queryResults.entries)),
    ...accessibleObjs(queryResults.folders).map((f) =>
      normalizeFolder(graphQLToREST('FolderWithPermissions', f))
    ),
    ...accessibleObjs(queryResults.bulkAssemblies),
    ...accessibleObjs(queryResults.collectibles),
    ...accessibleObjs(queryResults.connections),
    ...accessibleObjs(queryResults.datasets),
    ...accessibleObjs(queryResults.files),
    ...accessibleObjs(queryResults.jellyfishes),
    ...accessibleObjs(queryResults.processes),
    ...accessibleObjs(queryResults.processOutputs),
    ...accessibleObjs(queryResults.processTasks),
    ...accessibleObjs(queryResults.studies),
    ...accessibleObjs(queryResults.proteinAlignments),
    ...graphQLArrayToREST('ProtocolSummary', accessibleObjs(queryResults.protocols)),
    ...accessibleObjs(queryResults.reactions),
    ...graphQLArrayToREST('Request', accessibleObjs(queryResults.requests)),
    ...accessibleObjs(queryResults.requestV2Submissions),
    ...graphQLArrayToREST('Run', accessibleObjs(queryResults.runs)).map((run) => sidepanelRun(run)),
    ...accessibleObjs(queryResults.sequenceAlignments),
    ...accessibleObjs(queryResults.stageEntries),
    ...accessibleObjs(queryResults.storables).map((s) => graphQLStorableSummaryToREST(s)),
    ...accessibleObjs(queryResults.templateCollectionsByIds),
    ...accessibleObjs(queryResults.workflows),
    ...accessibleObjs(queryResults.worklists),
    ...accessibleObjs(queryResults.worksheets),
    ...accessibleObjs(queryResults.testDefinitions),
    ...accessibleObjs(queryResults.testOrders),
  ];
}

export function queryResultsToSchemas(
  queryResults: ItemListingSchemasQuery,
  filterSchemas?: SchemasFilterFunction
): {
  [modelType: string]: Array<Schema>;
} {
  return graphqlSchemasToItemListingFormat(queryResults, filterSchemas);
}

export type ItemListingSchema =
  | ItemListingRequestSchema
  | ItemListingResultSchema
  | ItemListingRunSchema
  | ItemListingBoxSchema
  | ItemListingConnectionSchema
  | ItemListingContainerSchema
  | ItemListingLocationSchema
  | ItemListingPlateSchema
  | ItemListingProcessTaskSchema
  | ItemListingProcessOutputSchema
  | ItemListingTagSchema
  | ItemListingStudySchema
  | TypesMenuSchema;

export interface ItemListingSchemasByType {
  [type: string]: Array<ItemListingSchema>;
}

export const searchableExclusiveSSIIds: Array<string> = [
  ANTIBODY_SCHEMA_INTERFACE_ID,
  EQUIPMENT_SCHEMA_INTERFACE_ID,
  ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID,
  ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID,
];

export interface ExclusiveSSIMap {
  [id: string]: string;
}

export function generateExclusiveSSIMap(exclusiveSSIData: TypesFilterTagSchemasSSIQuery): ExclusiveSSIMap {
  const exclusiveSSIMap: ExclusiveSSIMap = {};
  if (exclusiveSSIData && exclusiveSSIData.tagSchemas && exclusiveSSIData.tagSchemas.length > 0) {
    exclusiveSSIData.tagSchemas.forEach((tagSchema) => {
      const matchingConfig = tagSchema.interfaceConfigs.find((config) =>
        searchableExclusiveSSIIds.includes(config.interface.id)
      );
      if (matchingConfig) {
        exclusiveSSIMap[tagSchema.id] = matchingConfig.interface.id;
      }
    });
  }
  return exclusiveSSIMap;
}

// TODO(Search): Root out remaining references to the REST represenation for schemas that are nested
// within other components and elminate the need for this.
export interface ItemListingLegacySchemasByType {
  [type: string]: Array<Schema>;
}

export type SchemasFilterFunction = (schemas: ItemListingSchemasByType) => ItemListingSchemasByType;

function filterTagSchemasByExclusiveSSI<T extends ItemListingTagSchema | TypesFilterTagSchema>(
  schemas: Array<T>,
  exclusiveSSIMap: ExclusiveSSIMap,
  ssiId: string,
  modelType: string
): Array<T & {exclusiveSSITypeOverride: string}> {
  return schemas
    .filter((ts) => exclusiveSSIMap[ts.id] === ssiId)
    .map((ts) => ({...ts, exclusiveSSITypeOverride: modelType}));
}

export function groupSchemasByType(
  schemas: ItemListingSchemasQuery | TypesFilterSchemasQuery,
  exclusiveSSIData?: TypesFilterTagSchemasSSIQuery
): ItemListingSchemasByType {
  const exclusiveSSIMap = exclusiveSSIData ? generateExclusiveSSIMap(exclusiveSSIData) : {};

  // This type cast is a hack to work around a typescript issue where it thinks the filter signatures aren't
  // compatible between the two types, as described in https://github.com/microsoft/TypeScript/issues/33591.
  const filterableTagSchemas = schemas.tagSchemas as Array<ItemListingTagSchema | TypesFilterTagSchema>;
  // We want to categorize every schema as either DNA or oligo, so just get all schemas and
  // categorize ourselves. That way, we don't end up showing mixed schemas twice (which match
  // either DNA or oligo).
  const sequenceSchemas = filterableTagSchemas.filter((ts: ItemListingTagSchema | TypesFilterTagSchema) =>
    tagSchemaMatchesBasicFilters(ts as BasicFilterableTagSchema, {
      oligoAwareFolderItemTypes: [
        ModelTypeConstants.SEQUENCE,
        ModelTypeConstants.RNA_SEQUENCE,
        ModelTypeConstants.DNA_OLIGO,
        ModelTypeConstants.RNA_OLIGO,
      ],
      includeArchived: true,
    })
  );
  return {
    // -- groupSchemasByType TARGET FOR CODE GENERATION--
    [ModelTypeConstants.ANALYSIS]: [],
    [ModelTypeConstants.ANALYSIS_TEMPLATE]: [],
    [ModelTypeConstants.OLIGO_CONJUGATE]: filterTagSchemasByExclusiveSSI(
      filterableTagSchemas,
      exclusiveSSIMap,
      ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID,
      ModelTypeConstants.OLIGO_CONJUGATE
    ),
    [ModelTypeConstants.OLIGO_DUPLEX]: filterTagSchemasByExclusiveSSI(
      filterableTagSchemas,
      exclusiveSSIMap,
      ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID,
      ModelTypeConstants.OLIGO_DUPLEX
    ),
    [ModelTypeConstants.EQUIPMENT]: filterTagSchemasByExclusiveSSI(
      filterableTagSchemas,
      exclusiveSSIMap,
      EQUIPMENT_SCHEMA_INTERFACE_ID,
      ModelTypeConstants.EQUIPMENT
    ),
    [ModelTypeConstants.ANTIBODY]: filterTagSchemasByExclusiveSSI(
      filterableTagSchemas,
      exclusiveSSIMap,
      ANTIBODY_SCHEMA_INTERFACE_ID,
      ModelTypeConstants.ANTIBODY
    ),
    [ModelTypeConstants.ANALYTICS_DASHBOARD]: [],
    [ModelTypeConstants.ASSAY_RUN]: schemas.runSchemas,
    [ModelTypeConstants.SEQUENCE_ANALYSIS]: [],
    [ModelTypeConstants.PROTEIN_ALIGNMENT]: [],
    [ModelTypeConstants.BASIC_FOLDER_ITEM]: filterableTagSchemas.filter(
      (ts) =>
        !exclusiveSSIMap.hasOwnProperty(ts.id) &&
        tagSchemaMatchesBasicFilters(ts as BasicFilterableTagSchema, {
          oligoAwareFolderItemTypes: [ModelTypeConstants.BASIC_FOLDER_ITEM],
          includeArchived: true,
        })
    ),
    [ModelTypeConstants.BOX]: schemas.boxSchemas,
    [ModelTypeConstants.BULK_ASSEMBLY]: [],
    [ModelTypeConstants.CONNECTION]: schemas.connectionSchemas,
    [ModelTypeConstants.CONTAINER]: schemas.containerSchemas,
    [ModelTypeConstants.DATASET]: [],
    [ModelTypeConstants.ENTRY]: filterableTagSchemas.filter((ts) =>
      tagSchemaMatchesBasicFilters(ts as BasicFilterableTagSchema, {
        oligoAwareFolderItemTypes: [ModelTypeConstants.ENTRY],
        includeArchived: true,
      })
    ),
    [ModelTypeConstants.FILE]: [],
    [ModelTypeConstants.FOLDER]: [],
    [ModelTypeConstants.FORM_DEFINITION]: [],
    [ModelTypeConstants.JELLYFISH]: [],
    [ModelTypeConstants.LOCATION]: schemas.locationSchemas,
    [ModelTypeConstants.MIXTURE]: filterableTagSchemas.filter((ts) =>
      tagSchemaMatchesBasicFilters(ts as BasicFilterableTagSchema, {
        oligoAwareFolderItemTypes: [ModelTypeConstants.MIXTURE],
        includeArchived: true,
      })
    ),
    [ModelTypeConstants.MOLECULE]: filterableTagSchemas.filter((ts) =>
      tagSchemaMatchesBasicFilters(ts as BasicFilterableTagSchema, {
        oligoAwareFolderItemTypes: [ModelTypeConstants.MOLECULE],
        includeArchived: true,
      })
    ),
    [ModelTypeConstants.DNA_OLIGO]: sequenceSchemas.filter((ss) => isDNAOligoSchema(ss)),
    [ModelTypeConstants.RNA_OLIGO]: sequenceSchemas.filter((ss) => isRNAOligoSchema(ss)),
    [ModelTypeConstants.PLATE]: schemas.plateSchemas,
    [ModelTypeConstants.PROTEIN]: filterableTagSchemas.filter((ts) =>
      tagSchemaMatchesBasicFilters(ts as BasicFilterableTagSchema, {
        oligoAwareFolderItemTypes: [ModelTypeConstants.PROTEIN],
        includeArchived: true,
      })
    ),
    [ModelTypeConstants.PROCESS]: schemas.processTaskSchemasSupportingPlatformFeature,
    [ModelTypeConstants.PROCESS_TASK]: schemas.processTaskSchemasSupportingPlatformFeature,
    [ModelTypeConstants.PROCESS_OUTPUT]: schemas.processOutputSchemasSupportingPlatformFeature,
    [ModelTypeConstants.PROTOCOL]: [],
    [ModelTypeConstants.REACTION]: [],
    [ModelTypeConstants.REQUEST]: schemas.requestSchemas,
    [ModelTypeConstants.REQUEST_V2_SUBMISSION]: [],
    [ModelTypeConstants.REQUEST_V2_DEFINITION]: [],
    [ModelTypeConstants.RNA_SEQUENCE]: sequenceSchemas.filter((ss) => isRNASequenceSchema(ss)),
    [ModelTypeConstants.SEQUENCE]: sequenceSchemas.filter((ss) => isDNASequenceSchema(ss)),
    [ModelTypeConstants.STAGE_ENTRY]: [],
    [ModelTypeConstants.STUDY]: schemas.studySchemas,
    [ModelTypeConstants.WORKFLOW]: [],
    [ModelTypeConstants.WORKLIST]: [],
    [ModelTypeConstants.WORKSHEET]: [],
    [ModelTypeConstants.ASSAY_PROCEDURE]: [],
    [ModelTypeConstants.ASSAY_PROCESS]: [],
    [ModelTypeConstants.ASSAY_PROCESS_OUTPUT]: [],
    [ModelTypeConstants.ASSAY_RUN_TASK]: [],
    [ModelTypeConstants.ASSAY_UNIT_OPERATION_TASK]: [],
    [ModelTypeConstants.ASSAY_STEP_TASK]: [],
    [ModelTypeConstants.RECIPE_PROCEDURE]: [],
    [ModelTypeConstants.RECIPE_PROCESS]: [],
    [ModelTypeConstants.RECIPE_PROCESS_OUTPUT]: [],
    [ModelTypeConstants.RECIPE_RUN_TASK]: [],
    [ModelTypeConstants.RECIPE_UNIT_OPERATION_TASK]: [],
    [ModelTypeConstants.RECIPE_STEP_TASK]: [],
    [ModelTypeConstants.SUBTEMPLATE]: [],
    [ModelTypeConstants.TEMPLATE]: [],
    [ModelTypeConstants.TEMPLATE_COLLECTION]: [],
    [ModelTypeConstants.TEST_DEFINITION]: [],
    [ModelTypeConstants.TEST_ORDER]: [],
    [ModelTypeConstants.PREDICTION_BATCH]: [],
    // -- END groupSchemasByType TARGET FOR CODE GENERATION--
  };
}

function graphqlSchemasToItemListingFormat(
  schemas: ItemListingSchemasQuery,
  filterSchemas?: SchemasFilterFunction
): ItemListingLegacySchemasByType {
  let groupedSchemas = groupSchemasByType(schemas);
  if (filterSchemas) {
    groupedSchemas = filterSchemas(groupedSchemas);
  }
  // For ItemListing usage, some of the schemas need to be in the legacy REST format for downstream
  // consumers, so convert those here.
  return {
    ...groupedSchemas,
    [ModelTypeConstants.ASSAY_RUN]: graphQLArrayToREST(
      'RunSchema',
      groupedSchemas[ModelTypeConstants.ASSAY_RUN] as Array<ItemListingRunSchema>
    ),
    [ModelTypeConstants.BOX]: graphQLArrayToREST(
      'BoxSchema',
      groupedSchemas[ModelTypeConstants.BOX] as Array<ItemListingBoxSchema>
    ),
    [ModelTypeConstants.CONTAINER]: graphQLArrayToREST(
      'ContainerSchema',
      groupedSchemas[ModelTypeConstants.CONTAINER] as Array<ItemListingContainerSchema>
    ),
    [ModelTypeConstants.LOCATION]: graphQLArrayToREST(
      'LocationSchema',
      groupedSchemas[ModelTypeConstants.LOCATION] as Array<ItemListingLocationSchema>
    ),
    [ModelTypeConstants.PLATE]: graphQLArrayToREST(
      'PlateSchema',
      groupedSchemas[ModelTypeConstants.PLATE] as Array<ItemListingPlateSchema>
    ),
    [ModelTypeConstants.REQUEST]: graphQLArrayToREST(
      'RequestSchema',
      groupedSchemas[ModelTypeConstants.REQUEST] as Array<ItemListingRequestSchema>
    ),
  };
}

function tagSchemaSequenceTypeToSequenceSubtype(type: string): SequenceSubtype | null {
  switch (type) {
    case TagSchemaSequenceSchemaType.DNA_SEQUENCE:
      return ModelTypeConstants.SEQUENCE;
    case TagSchemaSequenceSchemaType.RNA_SEQUENCE:
      return ModelTypeConstants.RNA_SEQUENCE;
    case TagSchemaSequenceSchemaType.OLIGO:
      return ModelTypeConstants.DNA_OLIGO;
    case TagSchemaSequenceSchemaType.RNA_OLIGO:
      return ModelTypeConstants.RNA_OLIGO;
    default:
      return null;
  }
}

export function filterInterfacesByType(
  schemaInterfaces: Array<SchemaInterfaceExtendingSchema>,
  availableTypes: Array<string>
): Array<SchemaInterfaceExtendingSchema> {
  return schemaInterfaces.filter((schemaInterface) => {
    if (!schemaInterface.baseBioentityType) {
      return true;
    } else if (
      (schemaInterface.baseBioentityType.folderItemType as string) === (ModelTypeConstants.SEQUENCE as string)
    ) {
      const sequenceSubtype = tagSchemaSequenceTypeToSequenceSubtype(
        schemaInterface.baseBioentityType.sequenceType!
      );
      return sequenceSubtype != null && availableTypes.includes(sequenceSubtype);
    } else {
      return availableTypes.includes(schemaInterface.baseBioentityType.folderItemType);
    }
  });
}

export function addPermissionsAndTags(
  item: BaseItem,
  permissionsByItemId: Record<string, BenchlingPermissions>
): ListableItem {
  const listable = {
    ...item,
    // Table components expect all models to have tags
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    tags: (item as any).tags || [],
    permissions: permissionsByItemId[item.id],
  };

  return listable as ListableItem;
}

export function addAttributeValues(item: ListableItem, itemResult: ItemResult): ListableItem {
  return {
    ...item,
    attributeValues: itemResult.attributeValues,
  };
}
